import Phaser from 'phaser';
import { BIOMES_BY_ID } from '@/config/biomes.config';
import { useGameStore } from '@/ui/store/gameStore';
import { TELEPORT } from '@/config/balance.config';
import { RUNTIME_ASSET_LOAD_TIMEOUT_MS } from '@/game/assets/AssetBundleManager';

/**
 * Teleport FX — wspólny launcher widowiskowej `TeleportScene` (portal).
 *
 * Używany w 3 miejscach przejść między scenami:
 *   - WorldScene `biome:changed` → cel `WorldScene` (restart, nowy biom z save)
 *   - WorldScene `plaza:enter`   → cel `PlazaScene`
 *   - PlazaScene `plaza:exit`    → cel `WorldScene`
 *
 * **Dlaczego osobna scena-overlay (a nie tween w bieżącej scenie):**
 * podmiana scen niszczy bieżącą scenę i jej tweeny. Overlay (`TeleportScene`)
 * biegnie równolegle i przeżywa podmianę — wzorzec `HatchScene`.
 *
 * **Dlaczego `target` (deklaratywnie) zamiast callbacka `swap`:** podmianę scen
 * wykonuje SAMA `TeleportScene` przez swój własny plugin (zatrzymuje sceny
 * świata i launchuje cel na świeżo), zostając aktywna na wierzchu. Wcześniejsza
 * wersja wołała `scene.start()` z pluginu UMIERAJĄCEJ sceny w domknięciu — to
 * zostawiało listę scen niespójną (dwie sceny świata aktywne naraz) i
 * `TeleportScene` nie dawała się ponownie zlaunchować → 2-gi teleport się
 * zacinał, a UI znikało na stałe. Teraz lista scen zostaje spójna.
 */

export type TeleportTarget = 'WorldScene' | 'PlazaScene' | 'AtlantisSanctumScene' | 'HalloweenBossScene';

export interface TeleportData {
  /** Docelowa scena świata po teleporcie. */
  target: TeleportTarget;
  /** Kolor wiodący portalu (hex jako number) — motyw zależny od celu. */
  accent: number;
}

/** Ciepłe złoto placu handlowego (latarnie / NPC vibe). */
export const PLAZA_ACCENT = 0xffc94d;
/** Domyślny chłodny błękit, gdy nie znamy koloru celu. */
const DEFAULT_ACCENT = 0x6fd6ff;

/**
 * Bezpiecznik wall-clock: maksymalny czas, po którym flaga `isTeleporting`
 * jest twardo zerowana — NIEZALEŻNIE od cyklu życia sceny Phaser. Gwarantuje,
 * że UI nigdy nie zniknie „na zawsze", nawet gdyby maszyneria scen się zacięła.
 * = pełny czas portalu + spory zapas.
 */
// Dwie próby WorldScene mogą każda najpierw czekać na już aktywny loader,
// a potem na własny batch: 2 próby × 2 oczekiwania × timeout.
const SAFETY_MS = TELEPORT.coverMs + RUNTIME_ASSET_LOAD_TIMEOUT_MS * 4
  + TELEPORT.holdMs + TELEPORT.revealMs + 2000;

/** Monotoniczny token teleportu — bezpiecznik zeruje flagę tylko dla SWOJEGO teleportu. */
let seq = 0;

/**
 * Startuje portal teleportu. No-op, jeśli portal JUŻ jest aktywny (guard oparty
 * na realnym stanie sceny `isActive('TeleportScene')` — samonaprawialny: nie
 * blokuje na podstawie samej flagi, która teoretycznie mogłaby się zaciąć).
 */
export function startTeleport(scene: Phaser.Scene, data: TeleportData): void {
  if (scene.scene.isActive('TeleportScene')) return;

  const mySeq = ++seq;
  useGameStore.getState().setTeleporting(true);
  scene.scene.launch('TeleportScene', data);
  scene.scene.bringToTop('TeleportScene');

  // Bezpiecznik: jeśli portal z jakiegokolwiek powodu nie wyemituje SHUTDOWN,
  // twardo zatrzymujemy overlay po SAFETY_MS. Zeruje tylko stan SWOJEGO
  // teleportu (token), więc nie przerwie późniejszego przebiegu.
  window.setTimeout(() => {
    if (seq !== mySeq) return;
    try {
      if (scene.scene.isActive('TeleportScene')) {
        scene.scene.stop('TeleportScene');
      }
    } catch (err) {
      console.warn('[Teleport] Awaryjne zatrzymanie portalu nieudane:', err);
    } finally {
      useGameStore.getState().setTeleporting(false);
    }
  }, SAFETY_MS);
}

/** Kolor wiodący portalu dla danego biomu (z `scene.backgroundColor`). */
export function biomeAccent(biomeId: string): number {
  const hex = BIOMES_BY_ID[biomeId]?.scene.backgroundColor;
  if (!hex) return DEFAULT_ACCENT;
  return Phaser.Display.Color.HexStringToColor(hex).color;
}
